import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import _ from 'lodash';
import type { PersistenceLevel } from '@/core/flux.service';
import type { DerivedFeatureV1 } from '@/sdk/model/DerivedFeatureV1';
import type { ModelOutputV1 } from '@/sdk/model/ModelOutputV1';
import type { ReferencedOutputItemV1 } from '@/sdk/model/ReferencedOutputItemV1';
import type { ColumnDefinition } from '@/tableDefinitionEditor/tableDefinition.types';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import type { InputFeatureV1, OutputTypeEnum } from '@/sdk/model/InputFeatureV1';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { SeeqNames } from '@/main/app.constants.seeqnames';

export interface ModelInput {
  name: string;
  description?: string;
  referenceId?: string;
  id?: string;
  isCollapsed?: boolean;
  originalName?: string;
  columnDefinition?: ColumnDefinition;
  outputType?: OutputTypeEnum;
  inputType?: string;
  parent?: string;
  inputItem?: string;
  isMapped?: boolean;
  mappedFeatureId?: string;
  textInput?: string;
}

export interface ModelOutput extends ReferencedOutputItemV1 {
  id?: string;
  displayName?: string;
}

export type ModelItem = {
  name: string;
  description: string;
  id?: string;
  derivedFeatures: ColumnDefinition[] | ModelOutput[] | DerivedFeatureV1[];
  inputFeatures: ModelInput[];
  scopedTo?: string;
};

export type ModelPayload = Partial<
  Pick<ModelItem, 'name' | 'description' | 'id' | 'inputFeatures' | 'derivedFeatures' | 'scopedTo'>
>;

export class ModelStore extends BaseToolStore {
  persistenceLevel: PersistenceLevel = 'WORKSHEET';

  type = TREND_TOOLS.MODEL_CREATE;

  initialize() {
    this.state = this.immutable(
      _.assign({}, BASE_TOOL_COMMON_PROPS, {
        id: '',
        description: '',
        inputFeatures: [],
        derivedFeatures: [],
        name: '',
        modelOutput: undefined,
        scopedTo: undefined,
        featureToEditId: undefined,
        instanceToEditId: undefined,
      }),
    );
  }

  get id(): string {
    return this.state.get('id');
  }

  get name(): string {
    return this.state.get('name');
  }

  get scopedTo(): string | undefined {
    return this.state.get('scopedTo');
  }

  get description(): string {
    return this.state.get('description');
  }

  get derivedFeatures(): ModelOutput[] | ColumnDefinition[] | DerivedFeatureV1[] {
    return this.state.get('derivedFeatures');
  }

  get inputFeatures(): ModelInput[] {
    return this.state.get('inputFeatures');
  }

  get modelOutput(): ModelOutputV1 {
    return this.state.get('modelOutput');
  }

  get featureToEditId(): string | undefined {
    return this.state.get('featureToEditId');
  }

  get instanceToEditId(): string | undefined {
    return this.state.get('instanceToEditId');
  }

  dehydrate() {
    return this.state.serialize();
  }

  rehydrate(dehydratedState: any) {
    this.state.merge(dehydratedState);
  }

  protected readonly handlers = {
    ...this.baseHandlers,
  };

  resetModel = () => {
    this.initialize();
  };

  setFeatureToEditId = ({ featureToEditId }: { featureToEditId: string | undefined }) => {
    this.state.set('featureToEditId', featureToEditId);
  };

  setInstanceToEditId = ({ instanceToEditId }: { instanceToEditId: string | undefined }) => {
    this.state.set('instanceToEditId', instanceToEditId);
  };

  setModel = ({ model }: { model: ModelOutputV1 | ModelPayload }) => {
    this.state.set('modelOutput', model);

    if (model.description !== undefined) {
      this.state.set('description', model.description);
    }
    if (model.name !== undefined) {
      this.state.set('name', model.name);
    }
    if (model.id !== undefined) {
      this.state.set('id', model.id);
    }
    if (model.inputFeatures !== undefined) {
      // CRAB-53616: The instance name input feature must exist for all models, so ensure it is preserved when updating
      // input features. This implementation can change when we figure out the UX for model instance naming.
      const newInputFeatures = model.inputFeatures ?? [];
      const instanceName = this.state
        .get('inputFeatures')
        .find((f: InputFeatureV1) => f.name === SeeqNames.Models.InstanceNameInputFeatureName);

      this.state.set(
        'inputFeatures',
        instanceName && !newInputFeatures.some((f) => f.name === SeeqNames.Models.InstanceNameInputFeatureName)
          ? [...newInputFeatures, instanceName]
          : newInputFeatures,
      );
    }
    if (model.derivedFeatures !== undefined) {
      this.state.set('derivedFeatures', model.derivedFeatures ?? []);
    }
    if (model.scopedTo !== undefined) {
      this.state.set('scopedTo', model.scopedTo);
    }
  };
}
